import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import { BiddingType } from '@/common/enums/BiddingType.enum';
import BudgetModes from '@/common/enums/budget-modes.enum';
import LineItemOrigin from '@/common/enums/line-item-origin.enum';
import { Campaign, IABCategories, LineItem, PurchaseTypeOptions } from '@/common/models';
import Advertiser from '@/common/models/advertisers/Advertiser';
import { CampaignType } from '@/common/models/campaign/Campaign';
import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
import { convertRangesToDaypart, daypartToRanges, parseRanges } from '@/common/models/Dayparting';
import EntityProperty from '@/common/models/EntityProperty';
import LineItemOptions from '@/common/models/lineitem/LineItemOptions';
import Account from '@/common/models/misc-api-models/Account';
import PacingStrategyOptions from '@/common/models/PacingStrategyOptions';
import {
  Filter,
  FilterCollection,
  FilterComparisonOperator,
  FilterLogicalOperator,
  QueryParams,
} from '@/common/models/query-params';
import { FullDaypart, InvalidDaypart } from '@/globals';
import LineItemChannelTypeOptions from '@/org-app/components/lineItems/LineItemChannelTypeOptions';
import {
  useAccounts,
  useAdvertisers,
  useMediaOwners,
  useMediaTypes,
  useNetworks,
} from '@/org-app/services/tanstack/queries';
import { useUserOrganizations } from '@/org-app/services/tanstack/queries/userOrganizations.queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import {
  UPDATE_CAMPAIGN_ACCOUNT_ID,
  UPDATE_CAMPAIGN_ADVERTISER_ID,
  UPDATE_CAMPAIGN_NAME,
  UPDATE_LINEITEM_ADDURATION,
  UPDATE_LINEITEM_BUDGET,
  UPDATE_LINEITEM_BUDGET_MODE,
  UPDATE_LINEITEM_BUY_FROM_DEALS_ENABLED,
  UPDATE_LINEITEM_COMPANION_GROUP_NAME,
  UPDATE_LINEITEM_COMPANION_ORDER,
  UPDATE_LINEITEM_CREATIVEFORMATEXCLUSIONS,
  UPDATE_LINEITEM_CREATIVEFORMATS,
  UPDATE_LINEITEM_CREATIVEORIENTATIONEXCLUSIONS,
  UPDATE_LINEITEM_CREATIVEORIENTATIONS,
  UPDATE_LINEITEM_DAILY_GOAL_CAP,
  UPDATE_LINEITEM_DAYPART,
  UPDATE_LINEITEM_DELIVERY_CURVE,
  UPDATE_LINEITEM_DEMOGRAPHICIDS,
  UPDATE_LINEITEM_ENDDATE,
  UPDATE_LINEITEM_FLIGHT_RATIO,
  UPDATE_LINEITEM_GEOFENCE_GROUP_EXCLUSION_IDS,
  UPDATE_LINEITEM_GEOFENCE_GROUP_IDS,
  UPDATE_LINEITEM_IABCATEGORIES,
  UPDATE_LINEITEM_IABCATEGORYEXCLUSIONS,
  UPDATE_LINEITEM_IS_MEASUREMENT_REQUESTED,
  UPDATE_LINEITEM_IS_OFFLINE_ENABLED,
  UPDATE_LINEITEM_LANGUAGEEXCLUSIONIDS,
  UPDATE_LINEITEM_LANGUAGEIDS,
  UPDATE_LINEITEM_LOCATIONIDS,
  UPDATE_LINEITEM_MARGINS,
  UPDATE_LINEITEM_MARKET_IDS,
  UPDATE_LINEITEM_MARKETEXCLUSIONIDS,
  UPDATE_LINEITEM_MAXBIDCPM,
  UPDATE_LINEITEM_MEDIAOWNERIDS,
  UPDATE_LINEITEM_MEDIATYPEEXCLUSIONIDS,
  UPDATE_LINEITEM_MEDIATYPEIDS,
  UPDATE_LINEITEM_MINSECONDSBETWEENPLAYS,
  UPDATE_LINEITEM_NAME,
  UPDATE_LINEITEM_NETWORKEXCLUSIONIDS,
  UPDATE_LINEITEM_NETWORKIDS,
  UPDATE_LINEITEM_NOTES,
  UPDATE_LINEITEM_PACKAGE_ID,
  UPDATE_LINEITEM_PACKAGEGROUP_DEALS,
  UPDATE_LINEITEM_PRIORITY,
  UPDATE_LINEITEM_PURCHASE_TYPE,
  UPDATE_LINEITEM_SITEEXCLUSIONIDS,
  UPDATE_LINEITEM_SITEIDS,
  UPDATE_LINEITEM_STARTDATE,
  UPDATE_LINEITEM_TARGETIMPRESSIONS,
  UPDATE_LINEITEM_UNITEXCLUSIONIDS,
  UPDATE_LINEITEM_UNITIDS,
  UPDATE_LINEITEM_UNITPACKEXCLUSIONIDS,
  UPDATE_LINEITEM_UNITPACKIDS,
  UPDATE_LINEITEM_UNITTAGEXCLUSIONS,
  UPDATE_LINEITEM_UNITTAGS,
} from '@/store/modules/models/mutation-types';
import rootState from '@/store/state';
import { mapGetters } from '@/utils/StateMapping';
import i18n from '@/vueI18n';

export const getEntityProperties = () => {
  const type = 'LineItem';

  let isDemandOrganization = false;
  let isSupplyOrganization = false;
  if (rootState.orgApp != null && rootState.orgApp.organization != null) {
    isDemandOrganization = rootState.orgApp.organization.isDemandOrganization;
    isSupplyOrganization = rootState.orgApp.organization.isSupplyOrganization;
  }
  const getOrganizationSettings = () => rootState.orgApp.organizationSettings;

  const { hasChosenExchange } = mapGetters();

  const action = (lineItem: LineItem) =>
    rootStore.dispatch('orgApp/updateLineItem', { instance: lineItem, showLoader: false });
  const getExtraValidation = (lineItem: LineItem) =>
    lineItem.getExtraValidation(isDemandOrganization, getOrganizationSettings());
  const areDatesEditableHandler = (li: LineItem) => (li.isFromPackage ? isSupplyOrganization : true);
  const isFrequencyCapEditableHandler = (li: LineItem) => !li.hasCadencePurchaseType;
  const isPacingStrategyEditableHandler = (li: LineItem) => li.isPacingStrategyAvailable;

  const getMediaOwners = () => runQueryInScope(useMediaOwners);

  //
  // Logic to fetch markets, only one call will be performed.
  //
  const getMarkets = async () => {
    if (LineItem.isGettingMarkets) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!LineItem.isGettingMarkets && LineItem.marketsCache != null) {
            clearInterval(interval);
            resolve(LineItem.marketsCache);
          }
        }, 500);
      });
    }
    LineItem.isGettingMarkets = true;
    const mediaOwnerCountryRoots = await rootStore.dispatch('orgApp/getSupplierCountryRoots', { showLoader: false });
    const allMediaOwnerCountryIDs: number[][] = Object.values(mediaOwnerCountryRoots);
    const countryIDs: number[] = Array.from(new Set(allMediaOwnerCountryIDs.reduce((mem, x) => [...mem, ...x], [])));
    let ownerIDs: number[] = [];
    const { isSupplyOrganization } = rootState.orgApp.organization;
    if (isSupplyOrganization) {
      ownerIDs = [rootState.orgApp.organization.id];
    } else {
      const mediaOwners = await getMediaOwners();
      ownerIDs = mediaOwners.map((mediaOwner) => mediaOwner.id);
    }
    LineItem.marketsCache = await rootStore.dispatch('orgApp/getMarketsWithInventory', {
      ownerIDs,
      countryIDs,
      showLoader: false,
    });
    LineItem.isGettingMarkets = false;
    return LineItem.marketsCache;
  };

  //
  // Logic to fetch networks, only one call will be performed.
  //
  const getNetworks = () => runQueryInScope(useNetworks);

  //
  // Logic to fetch mediaTypes, only one call will be performed.
  //
  const getMediaTypes = () => runQueryInScope(useMediaTypes);

  //
  // Logic to fetch unitPacks, only one call will be performed.
  //
  const getUnitPacks = async () => {
    if (LineItem.isGettingUnitPacks) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!LineItem.isGettingUnitPacks && LineItem.unitPacksCache != null) {
            clearInterval(interval);
            resolve(LineItem.unitPacksCache);
          }
        }, 100);
      });
    }
    const queryParams = QueryParams.all(FilterCollection.for('active', [false]));
    LineItem.isGettingUnitPacks = true;
    LineItem.unitPacksCache = await rootStore.dispatch('orgApp/getUnitPacks', { queryParams, showLoader: false });
    LineItem.isGettingUnitPacks = false;
    return LineItem.unitPacksCache;
  };

  const budgetMode = new EntityProperty<LineItem>({
    label: capitalize(i18n.global.t('messages.deliverOn', { n: 2 })),
    property: 'budget_mode',
    type: TableColumnTypes.string,
    editablePropertyName: 'budgetMode',
    source: () => [
      {
        id: BudgetModes.BUDGET,
        name: capitalize(i18n.global.t('names.adSpend')),
      },
      {
        id: BudgetModes.IMPRESSIONS,
        name: capitalize(i18n.global.t('names.impression', { n: 2 })),
      },
    ],
  });

  const getLocationEntityProperty = (labelKey: string, level: number, isExclusion = false) => {
    return new EntityProperty({
      label: capitalize(i18n.global.t(labelKey, { n: 2 })),
      property: isExclusion ? 'location_exclusion_ids' : 'location_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: isExclusion ? 'locationExclusionIDs' : 'locationIDs',
      mutation: UPDATE_LINEITEM_LOCATIONIDS,
      editableClassName: type,
      source: ({ queryParams = QueryParams.all(FilterCollection.for('level', [level])) }) => {
        return rootStore.dispatch('orgApp/getLocations', queryParams);
      },
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [
              new Filter('level', [level]),
              new Filter(
                'location_id',
                isExclusion ? entity.locationExclusionIDs : entity.locationIDs,
                FilterComparisonOperator.IN,
              ),
            ],
            FilterLogicalOperator.AND,
          ),
        ),
      ignoreUnmatchedValuesInSource: true,
    });
  };

  return {
    id: EntityProperty.getIDProperty(capitalize(i18n.global.t('names.lineItemID', { n: 1 })), 'lineitem_id'),
    packageID: new EntityProperty<LineItem>({
      label: capitalize(i18n.global.t('names.dealID', { n: 1 })),
      property: 'package_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'packageID',
      mutation: UPDATE_LINEITEM_PACKAGE_ID,
      editableClassName: 'LineItem',
      bulkInputExamples: [i18n.global.t('messages.packageIDUseTheFollowingFormat')],
      action,
      getExtraValidation,
    }),
    name: new EntityProperty<LineItem>({
      label: capitalize(i18n.global.t('names.name', { n: 1 })),
      property: 'name',
      type: TableColumnTypes.string,
      editablePropertyName: 'name',
      mutation: UPDATE_LINEITEM_NAME,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    notes: new EntityProperty<LineItem>({
      label: capitalize(i18n.global.t('names.notes', { n: 1 })),
      property: 'notes',
      type: TableColumnTypes.string,
      editablePropertyName: 'notes',
      mutation: UPDATE_LINEITEM_NOTES,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    campaignID: EntityProperty.getIDProperty(capitalize(i18n.global.t('names.campaignID', { n: 1 })), 'campaign_id'),
    channelType: new EntityProperty({
      label: capitalize(i18n.global.t('names.channelType')),
      property: 'channel_type',
      type: TableColumnTypes.list,
      editablePropertyName: 'channelType',
      source: LineItemChannelTypeOptions.options().map((x) => ({
        id: x.value,
        name: x.name,
      })),
    }),
    status: new EntityProperty({
      label: capitalize(i18n.global.t('names.status', { n: 1 })),
      property: 'state',
      type: TableColumnTypes.string,
      source: (isDemandOrganization || !hasChosenExchange.value
        ? LineItemOptions.demandStatusOptions()
        : LineItemOptions.supplyStatusOptions()
      ).map((x) => ({
        id: x.value,
        name: x.name,
        filters: x.filters,
      })),
    }),
    pacing: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pacingVsExpected', { n: 1 })),
      property: 'cachedpacing.pacing',
      type: TableColumnTypes.percentage,
    }),
    progress: new EntityProperty({
      label: capitalize(i18n.global.t('names.progress', { n: 1 })),
      property: 'cachedpacing.progress',
      type: TableColumnTypes.percentage,
    }),
    cadence: new EntityProperty({
      label: capitalize(i18n.global.t('messages.targetedCadence')),
      property: 'cadence',
      type: TableColumnTypes.float,
    }),
    reversedPriority: new EntityProperty({
      label: capitalize(i18n.global.t('names.priority', { n: 1 })),
      property: 'priority',
      type: TableColumnTypes.integer,
      source: [1, 2, 3, 4, 5, 6, 7, 8, 9].map((x) => ({
        label: `${x}`,
        value: 10 - x,
      })),
      // sourceFilterProperty: 'priority',
      sourceLabel: 'label',
      sourceID: 'value',
      editablePropertyName: 'priority',
      editableClassName: type,
      mutation: UPDATE_LINEITEM_PRIORITY,
      action,
      getExtraValidation,
      defaultValue: 5,
    }),
    advertiser: new EntityProperty({
      label: capitalize(i18n.global.t('names.advertiser', { n: 1 })),
      property: 'campaign.advertiser.name',
      type: TableColumnTypes.list,
      source: () => runQueryInScope(useAdvertisers),
      sourceFilterProperty: 'campaign.advertiser_id',
      editablePropertyName: 'campaign.advertiserID',
      mutation: UPDATE_CAMPAIGN_ADVERTISER_ID,
      editableClassName: Campaign.entityName,
      postMutationCallback: (value?: Advertiser, entity?: LineItem) => {
        entity.campaign.advertiser = value;
      },
      getExtraValidation,
      isInlineEditingAvailable: false,
    }),
    totalImpressions: new EntityProperty({
      label: capitalize(i18n.global.t('names.impressionDelivered', { n: 2 })),
      property: 'cachedpacing.total_impressions',
      type: TableColumnTypes.integer,
    }),
    totalSpendOwner: new EntityProperty({
      label: capitalize(i18n.global.t('names.adSpend', { n: 1 })),
      property: 'cachedpacing.total_spend_owner',
      type: TableColumnTypes.currency,
    }),
    averageCPMOwner: new EntityProperty({
      label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
      property: 'cachedpacing.average_cpm_owner',
      type: TableColumnTypes.currency,
    }),
    totalSpendBuyer: new EntityProperty({
      label: capitalize(i18n.global.t('names.adSpend', { n: 1 })),
      property: 'cachedpacing.total_spend_buyer',
      type: TableColumnTypes.currency,
    }),
    averageCPMBuyer: new EntityProperty({
      label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
      property: 'cachedpacing.average_cpm_buyer',
      type: TableColumnTypes.currency,
    }),
    purchaseType: new EntityProperty({
      label: capitalize(i18n.global.t('names.purchaseType', { n: 1 })),
      property: 'purchase_type',
      type: TableColumnTypes.string,
      editablePropertyName: 'purchaseType',
      editableClassName: isDemandOrganization ? null : type,
      mutation: isDemandOrganization ? null : UPDATE_LINEITEM_PURCHASE_TYPE,
      source: (isDemandOrganization
        ? PurchaseTypeOptions.demandOrganizationOptions()
        : PurchaseTypeOptions.options()
      ).map((x) => ({
        id: x.value,
        name: x.name,
        filters: x.filters,
      })),
    }),
    cachedReportLastUpdatedOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.statsLastRefreshedOn')),
      property: 'cachedpacing.cached_report_last_updated_utc',
      type: TableColumnTypes.datetime,
    }),
    campaignName: new EntityProperty({
      label: capitalize(i18n.global.t('names.campaign', { n: 1 })),
      property: 'campaign.name',
      type: TableColumnTypes.string,
      mutation: UPDATE_CAMPAIGN_NAME,
      editablePropertyName: 'campaign.name',
      editableClassName: Campaign.entityName,
    }),
    account: new EntityProperty({
      label: capitalize(i18n.global.t('names.account', { n: 1 })),
      property: 'campaign.account.name',
      type: TableColumnTypes.list,
      source: () => runQueryInScope(useAccounts),
      sourceFilterProperty: 'campaign.account_id',
      editablePropertyName: 'campaign.accountID',
      mutation: UPDATE_CAMPAIGN_ACCOUNT_ID,
      editableClassName: Campaign.entityName,
      postMutationCallback: (value?: Account, entity?: LineItem) => {
        entity.campaign.account = value;
      },
    }),
    origin: new EntityProperty({
      label: capitalize(i18n.global.t('names.origin', { n: 1 })),
      property: 'package_id',
      type: TableColumnTypes.list,
      source: Object.keys(LineItemOrigin).map((key) => ({
        id: key,
        name: capitalize(i18n.global.t(`names.${LineItemOrigin[key]}`)),
      })),
      sourceFilterProperty: 'creation_origin',
    }),
    startDate: new EntityProperty({
      label: capitalize(i18n.global.t('names.startDate', { n: 1 })),
      property: 'flight_from',
      type: TableColumnTypes.date,
      editablePropertyName: 'startDate',
      mutation: UPDATE_LINEITEM_STARTDATE,
      editableClassName: type,
      action,
      isEditableHandler: areDatesEditableHandler,
      getExtraValidation,
    }),
    endDate: new EntityProperty({
      label: capitalize(i18n.global.t('names.endDate', { n: 1 })),
      property: 'flight_to',
      type: TableColumnTypes.date,
      editablePropertyName: 'endDate',
      mutation: UPDATE_LINEITEM_ENDDATE,
      editableClassName: type,
      action,
      isEditableHandler: areDatesEditableHandler,
      getExtraValidation,
    }),
    budgetMode,
    flightRatio: new EntityProperty({
      label: capitalize(i18n.global.t('names.flightGoalRatio', { n: 1 })),
      property: 'flight_goal_ratio',
      editablePropertyName: 'flightRatio',
      editableClassName: type,
      type: TableColumnTypes.integer,
      mutation: UPDATE_LINEITEM_FLIGHT_RATIO,
      action: (lineItem: LineItem) =>
        rootStore.dispatch('orgApp/updateLineItem', {
          // @ts-expect-error flight_goal_ratio is not a property of LineItem...
          instance: { ...lineItem, flight_goal_ratio: lineItem.flight_goal_ratio / 100 },
          showLoader: false,
        }),
    }),
    advertiserBudget: new EntityProperty({
      label: capitalize(i18n.global.t('names.advertiserBudget')),
      property: 'advertiser_budget',
      type: TableColumnTypes.currency,
      editablePropertyName: 'advertiserBudget',
      action,
      getExtraValidation,
    }),
    budget: new EntityProperty({
      label: capitalize(i18n.global.t('names.budget', { n: 1 })),
      property: 'budget',
      type: TableColumnTypes.currency,
      editablePropertyName: 'budget',
      mutation: UPDATE_LINEITEM_BUDGET,
      editableClassName: type,
      action,
      getExtraValidation,
      isEditableHandler: (li: LineItem) =>
        li.isBudgetEditable && li.isBudgetModeAmount && (li.advertiserBudget == null || li.advertiserBudget === 0),
      preventEditingEntityProperties: ['market_targeted_impressions'],
      preMutationTransform: (x) => (x != null ? Number.parseFloat(String(x).replace(/,/g, '')) : null),
      postMutationCallback: (value, entity) => {
        if (value == null) return;
        rootStore.commit(UPDATE_LINEITEM_BUDGET_MODE, {
          lineItem: entity,
          value: BudgetModes.BUDGET,
        });
      },
      mutatesProperties: [budgetMode],
    }),
    targetImpressions: new EntityProperty({
      label: capitalize(i18n.global.t('messages.impressionGoal')),
      property: 'market_targeted_impressions',
      type: TableColumnTypes.integer,
      editablePropertyName: 'targetImpressions',
      mutation: UPDATE_LINEITEM_TARGETIMPRESSIONS,
      editableClassName: type,
      action,
      getExtraValidation,
      isEditableHandler: (li) => li.isMarketTargetedImpressionsEditable && li.isBudgetModeImpressions,
      preventEditingEntityProperties: ['budget'],
      preMutationTransform: (x) => (x != null ? Number.parseInt(x, 10) : null),
      postMutationCallback: (value, entity) => {
        if (value == null) return;
        rootStore.commit(UPDATE_LINEITEM_BUDGET_MODE, {
          lineItem: entity,
          value: BudgetModes.IMPRESSIONS,
        });
      },
      mutatesProperties: [budgetMode],
    }),
    minSecondsBetweenPlays: new EntityProperty({
      label: capitalize(i18n.global.t('names.frequencyCap', { n: 1 })),
      property: 'min_seconds_between_plays',
      type: TableColumnTypes.integer,
      isEditableHandler: isFrequencyCapEditableHandler,
      editablePropertyName: 'minSecondsBetweenPlays',
      mutation: UPDATE_LINEITEM_MINSECONDSBETWEENPLAYS,
      editableClassName: type,
      action,
      getExtraValidation,
      defaultValue: 0,
      toStringOverride: (entity) => entity.frequencyCapFormatted,
    }),
    modifiedOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.lastModifiedOn')),
      property: 'modified_on_utc',
      type: TableColumnTypes.datetime,
    }),
    createdBy: new EntityProperty({
      label: capitalize(i18n.global.t('messages.createdBy')),
      property: 'created_by',
      type: TableColumnTypes.list,
      source: () => runQueryInScope(useUserOrganizations),
      sourceFilterProperty: 'created_by',
      sourceLabel: 'email',
      sourceID: 'userID',
    }),
    dailyGoalCap: new EntityProperty({
      label: capitalize(i18n.global.t('messages.dailyBudgetCap')),
      property: 'cap',
      type: (lineItem: LineItem) => {
        return lineItem.budgetMode === BudgetModes.BUDGET ? TableColumnTypes.currency : TableColumnTypes.integer;
      },
      editablePropertyName: 'dailyGoalCap',
      mutation: UPDATE_LINEITEM_DAILY_GOAL_CAP,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    maxBidCPM: new EntityProperty({
      label: (lineItem) => {
        const key = lineItem?.biddingType === BiddingType.Fixed ? 'messages.fixedBidCpm' : 'names.maxBidCPM';
        return capitalize(i18n.global.t(key, { n: 1 }));
      },
      property: 'max_bid_cpm',
      type: TableColumnTypes.currency,
      editablePropertyName: 'maxBidCPM',
      mutation: UPDATE_LINEITEM_MAXBIDCPM,
      editableClassName: type,
      action,
      getExtraValidation,
      preMutationTransform: (x) => (x != null ? Number.parseFloat(x) : null),
    }),
    audienceFile: new EntityProperty({
      label: capitalize(i18n.global.t('names.audienceFile', { n: 1 })),
      property: 'demographic_ids',
      type: TableColumnTypes.integer,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'demographicIDs',
      mutation: UPDATE_LINEITEM_DEMOGRAPHICIDS,
      editableClassName: type,
      source: () =>
        rootStore.dispatch('orgApp/getDemographics', {
          queryParams: QueryParams.all(FilterCollection.for('type', ['AUDIENCE-FILE'])),
        }),
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
    }),
    demographics: new EntityProperty({
      label: capitalize(i18n.global.t('names.demographic', { n: 2 })),
      property: 'demographic_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'demographicIDs',
      mutation: UPDATE_LINEITEM_DEMOGRAPHICIDS,
      editableClassName: type,
      source: ({ queryParams = QueryParams.all() }) => {
        return rootStore.dispatch('orgApp/getDemographics', queryParams);
      },
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(FilterCollection.for('demographic_id', entity.demographicIDs)),
    }),
    deliveryCurve: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pacingStrategy', { n: 1 })),
      property: 'delivery_curve',
      type: TableColumnTypes.string,
      editablePropertyName: 'deliveryCurve',
      mutation: UPDATE_LINEITEM_DELIVERY_CURVE,
      editableClassName: type,
      source: new PacingStrategyOptions().getStrategiesForInlineEditing(),
      action,
      getExtraValidation,
      isEditableHandler: isPacingStrategyEditableHandler,
      defaultValue: {
        label: PacingStrategyOptions.options().find((x) => x.id === 'audience').name,
        value: 'audience',
      },
    }),
    markets: new EntityProperty({
      label: capitalize(i18n.global.t('names.market', { n: 2 })),
      property: 'market_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
      sourceFilterProperty: 'market_id',
      editablePropertyName: 'marketIDs',
      mutation: UPDATE_LINEITEM_MARKET_IDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    marketExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.marketExclusion', { n: 2 })),
      property: 'market_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
      sourceFilterProperty: 'market_id',
      editablePropertyName: 'marketExclusionIDs',
      mutation: UPDATE_LINEITEM_MARKETEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networks: new EntityProperty({
      label: capitalize(i18n.global.t('names.network', { n: 2 })),
      property: 'network_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getNetworks,
      sourceFilterProperty: 'network_id',
      editablePropertyName: 'networkIDs',
      mutation: UPDATE_LINEITEM_NETWORKIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networkExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.networkExclusion', { n: 2 })),
      property: 'network_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getNetworks,
      sourceFilterProperty: 'network_id',
      editablePropertyName: 'networkExclusionIDs',
      mutation: UPDATE_LINEITEM_NETWORKEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    unitPacks: new EntityProperty({
      label: capitalize(i18n.global.t('names.unitPack', { n: 2 })),
      property: 'unitpack_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getUnitPacks,
      sourceFilterProperty: 'unitpack_id',
      editablePropertyName: 'unitPackIDs',
      mutation: UPDATE_LINEITEM_UNITPACKIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    unitPackExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.unitPackExclusion', { n: 2 })),
      property: 'unitpack_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getUnitPacks,
      sourceFilterProperty: 'unitpack_id',
      editablePropertyName: 'unitPackExclusionIDs',
      mutation: UPDATE_LINEITEM_UNITPACKEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    mediaOwners: new EntityProperty({
      label: capitalize(i18n.global.t('names.mediaOwner', { n: 2 })),
      property: 'mediaowner_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaOwners,
      sourceFilterProperty: 'owner_id',
      editablePropertyName: 'mediaOwnerIDs',
      mutation: UPDATE_LINEITEM_MEDIAOWNERIDS,
      editableClassName: type,
      action,
      getExtraValidation,
      defaultValue: async () => (await getMediaOwners()).map((x) => ({ label: x.name, value: x.id })),
    }),
    mediaTypes: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueType', { n: 2 })),
      property: 'mediatype_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
      editablePropertyName: 'mediaTypeIDs',
      mutation: UPDATE_LINEITEM_MEDIATYPEIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    mediaTypeExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueTypeExclusion', { n: 2 })),
      property: 'mediatype_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
      editablePropertyName: 'mediaTypeExclusionIDs',
      mutation: UPDATE_LINEITEM_MEDIATYPEEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    daypart: new EntityProperty({
      label: capitalize(i18n.global.t('names.daypart', { n: 2 })),
      property: 'daypart',
      type: TableColumnTypes.string,
      editablePropertyName: 'daypartRanges',
      validationPropertyName: 'daypart',
      mutation: UPDATE_LINEITEM_DAYPART,
      editableClassName: type,
      preMutationTransform: (text) => {
        if (text == null || text === '') return FullDaypart;
        try {
          const result = parseRanges(text);
          return convertRangesToDaypart(result.ranges);
        } catch (e) {
          return InvalidDaypart;
        }
      },
      action,
      getExtraValidation,
      canAffectAdDeliveryWhenModified: true,
      bulkInputNote: 'Note that hours are inclusive.',
      bulkInputExamples: ['Mon-Sun 6-17', 'Mon 12-16 Tue-Fri 8-9'],
      defaultValue: {
        label: daypartToRanges(FullDaypart),
        value: FullDaypart,
      },
    }),
    campaignType: new EntityProperty({
      label: capitalize(i18n.global.t('names.campaignType', { n: 1 })),
      property: 'campaign.campaign_type',
      type: TableColumnTypes.list,
      source: () =>
        new Promise((resolve) =>
          resolve(
            Object.keys(CampaignType).map((key, i) => ({
              id: i,
              name: capitalize(CampaignType[key].replace('_', ' ').toLowerCase()),
              value: CampaignType[key],
              logicalOperator: FilterLogicalOperator.AND,
              filters: [new Filter('campaign.campaign_type', [CampaignType[key]], FilterComparisonOperator.EQUAL)],
            })),
          ),
        ),
    }),
    creativeOrientations: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientation', { n: 2 })),
      property: 'creative_orientations',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientations',
      mutation: UPDATE_LINEITEM_CREATIVEORIENTATIONS,
      editableClassName: type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeOrientationExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientationExclusion', { n: 2 })),
      property: 'creative_orientation_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientationExclusions',
      mutation: UPDATE_LINEITEM_CREATIVEORIENTATIONEXCLUSIONS,
      editableClassName: type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormats: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormat', { n: 2 })),
      property: 'creative_formats',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormats',
      mutation: UPDATE_LINEITEM_CREATIVEFORMATS,
      editableClassName: type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormatExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormatExclusion', { n: 2 })),
      property: 'creative_format_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormatExclusions',
      mutation: UPDATE_LINEITEM_CREATIVEFORMATEXCLUSIONS,
      editableClassName: type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    unitTags: new EntityProperty({
      label: capitalize(i18n.global.t('names.tag', { n: 2 })),
      property: 'unit_tags',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTags',
      mutation: UPDATE_LINEITEM_UNITTAGS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    unitTagExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.tagExclusion', { n: 2 })),
      property: 'unit_tag_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTagExclusions',
      mutation: UPDATE_LINEITEM_UNITTAGEXCLUSIONS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    countries: getLocationEntityProperty('names.country', 0),
    regions: getLocationEntityProperty('names.region', 1),
    cities: getLocationEntityProperty('names.city', 3),
    countryExclusions: getLocationEntityProperty('names.countryExclusion', 0, true),
    regionExclusions: getLocationEntityProperty('names.regionExclusion', 1, true),
    cityExclusions: getLocationEntityProperty('names.cityExclusion', 3, true),
    languages: new EntityProperty({
      label: capitalize(i18n.global.t('names.language', { n: 2 })),
      property: 'language_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'languageIDs',
      mutation: UPDATE_LINEITEM_LANGUAGEIDS,
      editableClassName: type,
      source: () => rootStore.dispatch('orgApp/getLanguages'),
      action,
      getExtraValidation,
    }),
    languageExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.languageExclusion', { n: 2 })),
      property: 'language_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'languageExclusionIDs',
      mutation: UPDATE_LINEITEM_LANGUAGEEXCLUSIONIDS,
      editableClassName: type,
      source: () => rootStore.dispatch('orgApp/getLanguages'),
      action,
      getExtraValidation,
    }),
    sites: new EntityProperty({
      label: capitalize(i18n.global.t('names.site', { n: 2 })),
      property: 'site_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteIDs',
      mutation: UPDATE_LINEITEM_SITEIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    siteExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.siteExclusion', { n: 2 })),
      property: 'site_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteExclusionIDs',
      mutation: UPDATE_LINEITEM_SITEEXCLUSIONIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    screens: new EntityProperty({
      label: capitalize(i18n.global.t('names.screen', { n: 2 })),
      property: 'unit_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitIDs',
      mutation: UPDATE_LINEITEM_UNITIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    screenExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.screenExclusion', { n: 2 })),
      property: 'unit_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitExclusionIDs',
      mutation: UPDATE_LINEITEM_UNITEXCLUSIONIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroups: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroup', { n: 2 })),
      property: 'geofencegroup_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupIDs',
      mutation: UPDATE_LINEITEM_GEOFENCE_GROUP_IDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroupExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroupExclusion', { n: 2 })),
      property: 'geofencegroup_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupExclusionIDs',
      mutation: UPDATE_LINEITEM_GEOFENCE_GROUP_EXCLUSION_IDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    customFields: new EntityProperty({
      label: capitalize(i18n.global.t('names.customField', { n: 2 })),
      property: 'customFields',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'customFields',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    lineItemMargins: new EntityProperty({
      label: capitalize(i18n.global.t('names.margin', { n: 2 })),
      property: 'lineitem_margins',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'margins',
      mutation: UPDATE_LINEITEM_MARGINS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    adDurationType: new EntityProperty({
      label: capitalize(i18n.global.t('names.adDurationType', { n: 1 })),
      property: 'adDurationTypeDescription',
      type: TableColumnTypes.list,
      action,
      getExtraValidation,
    }),
    adDuration: new EntityProperty({
      label: capitalize(i18n.global.t('names.adDuration', { n: 1 })),
      property: 'ad_duration',
      type: TableColumnTypes.integer,
      editablePropertyName: 'adDuration',
      mutation: UPDATE_LINEITEM_ADDURATION,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    isMeasurementRequested: new EntityProperty({
      label: capitalize(i18n.global.t('actions.includeLiftAnalysis')),
      property: 'measurement_requested',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isMeasurementRequested',
      editableClassName: type,
      mutation: UPDATE_LINEITEM_IS_MEASUREMENT_REQUESTED,
      action,
      getExtraValidation,
    }),
    buyFromDealsEnabled: new EntityProperty({
      label: capitalize(i18n.global.t('messages.buyFromDeal')),
      property: 'buy_from_deals_enabled',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'buyFromDealsEnabled',
      editableClassName: type,
      mutation: UPDATE_LINEITEM_BUY_FROM_DEALS_ENABLED,
      action,
      getExtraValidation,
    }),
    deal: new EntityProperty({
      label: capitalize(i18n.global.t('names.deal')),
      property: 'dealIDFormatted',
      type: TableColumnTypes.string,
      editablePropertyName: 'dealIDFormatted',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    deals: new EntityProperty({
      label: capitalize(i18n.global.t('names.deal', { n: 2 })),
      property: 'packageGroupDealsIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'packageGroupDealsIDs',
      editableClassName: type,
      mutation: UPDATE_LINEITEM_PACKAGEGROUP_DEALS,
      action,
      getExtraValidation,
    }),
    externalID: new EntityProperty({
      label: capitalize(i18n.global.t('names.externalID')),
      property: 'external_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'externalID',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaWeather: new EntityProperty({
      label: capitalize(i18n.global.t('names.weatherCondition', { n: 2 })),
      property: 'targetCriteriaWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('names.temperature', { n: 1 })),
      property: 'targetCriteriaTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaUV: new EntityProperty({
      label: capitalize(i18n.global.t('messages.uvTargeting', { n: 1 })),
      property: 'targetCriteriaUV',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaPollen: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pollenTargeting', { n: 1 })),
      property: 'targetCriteriaPollen',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationWeather: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetWeatherConditions')),
      property: 'targetCriteriaDestinationWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetTemperature')),
      property: 'targetCriteriaDestinationTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationRain: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetRainAccumulation')),
      property: 'targetCriteriaDestinationRain',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationSnow: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetSnowAccumulation')),
      property: 'targetCriteriaDestinationSnow',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaCustomEvents: new EntityProperty({
      label: capitalize(i18n.global.t('names.customEvent', { n: 2 })),
      property: 'targetCriteriaCustomEvents',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    minConcentrationValue: new EntityProperty({
      label: capitalize(i18n.global.t('messages.audienceConcentrationThreshold', { n: 1 })),
      property: 'min_concentration_value',
      type: TableColumnTypes.float,
      editablePropertyName: 'minConcentrationValue',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    minConcentrationFactor: new EntityProperty({
      label: capitalize(i18n.global.t('messages.audienceConcentrationRatio')),
      property: 'min_concentration_factor',
      type: TableColumnTypes.string,
      editablePropertyName: 'minConcentrationFactor',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    isCPMFromRateCard: new EntityProperty({
      label: capitalize(i18n.global.t('messages.isCPMFromRateCard')),
      property: 'is_cpm_from_rate_card',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isCPMFromRateCard',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    CPM: new EntityProperty({
      label: capitalize(i18n.global.t('names.cpm')),
      property: 'CPM',
      type: TableColumnTypes.float,
      // editablePropertyName: 'cpm',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networkFrequencyCaps: new EntityProperty({
      label: capitalize(i18n.global.t('messages.enableNetworkFrequencyCap')),
      property: 'network_frequency_caps',
      type: TableColumnTypes.list,
      editablePropertyName: 'networkFrequencyCaps',
      editableClassName: type,
      editablePropertyAcceptsMultipleValues: true,
      action,
      getExtraValidation,
    }),
    entityFees: new EntityProperty({
      label: capitalize(i18n.global.t('names.mediaFee', { n: 2 })),
      property: 'entity_fees',
      type: TableColumnTypes.list,
      editablePropertyName: 'entityFees',
      editableClassName: type,
      editablePropertyAcceptsMultipleValues: true,
      action,
      getExtraValidation,
    }),
    companionGroupName: new EntityProperty({
      label: capitalize(i18n.global.t('names.companionAdGroupName', { n: 1 })),
      property: 'companion_group_name',
      type: TableColumnTypes.string,
      mutation: UPDATE_LINEITEM_COMPANION_GROUP_NAME,
      editablePropertyName: 'companionGroupName',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    companionOrder: new EntityProperty({
      label: capitalize(i18n.global.t('names.companionAdOrder', { n: 1 })),
      property: 'companion_order',
      type: TableColumnTypes.integer,
      mutation: UPDATE_LINEITEM_COMPANION_ORDER,
      editablePropertyName: 'companionOrder',
      editableClassName: type,
      source: [
        { id: -1, name: capitalize(i18n.global.t('states.before')) },
        { id: 0, name: capitalize(i18n.global.t('names.primary')) },
        { id: 1, name: capitalize(i18n.global.t('states.after')) },
      ],
      action,
      getExtraValidation,
    }),
    iabCategories: new EntityProperty({
      label: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
      property: 'iab_categories',
      type: TableColumnTypes.list,
      source: IABCategories.hierarchicalOptions(),
      sourceFilterProperty: 'iab_categories',
      sourceGroupValues: 'subCategories',
      sourceGroupLabel: 'name',
      areSourceGroupParentsSelectable: true,
      editablePropertyName: 'iabCategories',
      mutation: UPDATE_LINEITEM_IABCATEGORIES,
      editableClassName: type,
      editablePropertyAcceptsMultipleValues: true,
      preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
      action,
      getExtraValidation,
    }),
    iabCategoryExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.iabCategoryExclusion', { n: 2 })),
      property: 'iab_category_exclusions',
      type: TableColumnTypes.list,
      source: IABCategories.hierarchicalOptions(),
      sourceFilterProperty: 'iab_categories',
      sourceGroupValues: 'subCategories',
      sourceGroupLabel: 'name',
      areSourceGroupParentsSelectable: true,
      editablePropertyName: 'iabCategoryExclusions',
      mutation: UPDATE_LINEITEM_IABCATEGORYEXCLUSIONS,
      editableClassName: type,
      editablePropertyAcceptsMultipleValues: true,
      preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
      action,
      getExtraValidation,
    }),
    displayAndVideoDeals: new EntityProperty({
      label: capitalize(i18n.global.t('names.deal', { n: 2 })),
      property: 'dealIDsBySsp',
      type: TableColumnTypes.string,
    }),
    postalCodesFile: new EntityProperty({
      label: capitalize(i18n.global.t('names.postalCodesFile')),
      property: 'postalCodesFile',
      type: TableColumnTypes.string,
    }),
    biddingType: new EntityProperty({
      label: capitalize(i18n.global.t('names.biddingStrategy')),
      property: 'biddingType',
      type: TableColumnTypes.string,
    }),
    isOfflineEnabled: new EntityProperty({
      label: capitalize(i18n.global.t('messages.isAvailableForOfflinePlays')),
      property: 'is_offline_enabled',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isOfflineEnabled',
      editableClassName: type,
      mutation: UPDATE_LINEITEM_IS_OFFLINE_ENABLED,
      action,
      getExtraValidation,
    }),
  };
};
